import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { useSgwtTranslation } from '../../../common/i18n';
import { registerChatbotEvent } from '../../../common/monitoring';
import type { SgwtChatbotAdapter, SgwtChatbotContext } from '../../../common/types/sgwt-chatbot.adapter';
import type { SgwtHelpCenterPublicFields } from '../../sgwt-help-center/sgwt-help-center';
import { botConversationReducer, createInitialState } from './bot-conversation.reducer';
import type {
  ConversationMessage,
  SgwtChatbotConversation,
  SgwtChatbotConversationItem,
  SgwtChatbotMessage,
  UserFeedback,
  UserFeedbackReaction,
} from './chatbot.types';
import { scrollToLatestMessage } from './chatbot.utils';

export type ChatbotPanelDisplayed = 'conversation' | 'history';

interface BotConversationContextProps {
  // error: Error | null;
  history: SgwtChatbotConversation[] | undefined;
  messages: SgwtChatbotConversationItem[];
  sendMessage: (message: string) => void;
  startNewConversation: () => void;
  switchToConversation: (conversationId: string) => void;
  panelDisplayed: ChatbotPanelDisplayed;
  changePanelDisplayed: (panel: ChatbotPanelDisplayed) => void;
  waitingBot: boolean;
  inputText: string;
  setInputText: (inputText: string) => void;
  displayContactUsButton: boolean;
  feedbackContactUs: (gotoContactUs: boolean) => void;
  addReaction: (message: SgwtChatbotMessage, reaction: UserFeedbackReaction) => void;
  sendFeedback: (feedback: UserFeedback) => void;
  cancelFeedback: (feedback: UserFeedback) => void;
  isEmbeddedInHelpCenter: boolean;
}

const defaultOptions: BotConversationContextProps = {
  history: undefined,
  messages: [],
  sendMessage: (_message: string) => {},
  switchToConversation: (_conversationId: string) => {},
  panelDisplayed: 'conversation',
  startNewConversation: () => {},
  changePanelDisplayed: (_panel: ChatbotPanelDisplayed) => {},
  waitingBot: false,
  inputText: '',
  setInputText: (_inputText: string) => {},
  displayContactUsButton: false,
  feedbackContactUs: (_gotoContactUs: boolean) => {},
  addReaction: (_message: SgwtChatbotMessage, _reaction: UserFeedbackReaction) => {},
  sendFeedback: (_feedback: UserFeedback) => {},
  cancelFeedback: (_feedback: UserFeedback) => {},
  isEmbeddedInHelpCenter: false,
};

export const BotConversationContext = createContext<BotConversationContextProps>(defaultOptions);

interface BotConversationProviderProps {
  botAdapter: SgwtChatbotAdapter | undefined;
  chatBeforeContactUs?: boolean;
  embeddedInHelpCenter: boolean;
  chatbotContext: SgwtChatbotContext;
  conversationId?: string;
  children?: ReactNode;
  initialMessage?: string;
  onNewMessage?: (message: SgwtChatbotMessage) => void;
}

const noAdapterError = (operation: string) => {
  console.warn(`<sgwt-chatbot> No bot adapter set, cannot ${operation}.`);
  return new Error('Configuration issue - no chatbot adapter set');
};

const supportFeedback = (botAdapter: SgwtChatbotAdapter | undefined): boolean => {
  return typeof botAdapter?.onUserFeedback === 'function';
};

export const BotConversationProvider = ({
  children,
  botAdapter,
  chatBeforeContactUs,
  embeddedInHelpCenter,
  chatbotContext,
  conversationId,
  initialMessage,
  onNewMessage,
}: BotConversationProviderProps) => {
  const [context, dispatch] = useReducer(botConversationReducer, null, () => createInitialState());
  const [panelDisplayed, setPanelDisplayed] = useState<ChatbotPanelDisplayed>('conversation');
  const { translate } = useSgwtTranslation();
  const [inputText, setInputText] = useState('');
  const [displayContactUsButton, setDisplayContactUsButton] = useState(false);
  const initialMessageSent = useRef(false);
  const conversationIdRetrieved = useRef(false);
  const contactUsWelcomeRef = useRef(false);

  const count = useMemo(() => {
    const user = context.messages.filter((msg) => msg.type === 'message' && msg.content.role === 'user').length;
    const bot = context.messages.filter((msg) => msg.type === 'message' && msg.content.role === 'bot').length;
    return { user, bot };
  }, [context.messages]);

  useEffect(() => {
    if (botAdapter) {
      botAdapter.setPublishMessageHandler?.((message: SgwtChatbotMessage) => {
        dispatch({ type: 'chatbot.receive-message', payload: { response: message, allowFeedback: supportFeedback(botAdapter) } });
        onNewMessage?.(message);
        scrollToLatestMessage();
        registerChatbotEvent(`conversation.${message.role}.message`, { conversationId: message.conversationId });
      });
    }
  }, [botAdapter, onNewMessage]);

  // Send a message to the bot
  const sendMessage = useCallback(
    (message: string) => {
      const newMessage: SgwtChatbotMessage = {
        conversationId: context.conversationId || undefined,
        role: 'user',
        message: {
          content: message,
          format: 'text',
        },
      };

      // Add the user message to the conversation
      dispatch({ type: 'chatbot.send-message', payload: newMessage });
      onNewMessage?.(newMessage);
      scrollToLatestMessage();
      registerChatbotEvent(`conversation.${newMessage.role}.message`, { conversationId: newMessage.conversationId });

      if (botAdapter) {
        botAdapter
          .onUserMessage(chatbotContext, newMessage)
          .then((response) => {
            if (response) {
              dispatch({ type: 'chatbot.receive-message', payload: { response, allowFeedback: supportFeedback(botAdapter) } });
              onNewMessage?.(response);
              scrollToLatestMessage();
              registerChatbotEvent(`conversation.${response.role}.message`, { conversationId: response.conversationId });
            }
          })
          .catch((error) => dispatch({ type: 'chatbot.error-occured', payload: error }));
      } else {
        dispatch({ type: 'chatbot.error-occured', payload: noAdapterError('send user message') });
      }
    },
    [botAdapter, context.conversationId, onNewMessage, chatbotContext],
  );

  // Switch to an existing conversation from history
  const switchToConversation = useCallback(
    (conversationId: string) => {
      if (botAdapter) {
        botAdapter
          .getConversation(chatbotContext, conversationId)
          .then((data) => {
            if (data) {
              dispatch({ type: 'chatbot.switch-conversation', payload: data });
              registerChatbotEvent('conversation.switch-conversation', { conversationId });
            }
          })
          .catch((error) => dispatch({ type: 'chatbot.error-occured', payload: error }))
          .finally(() => {
            setPanelDisplayed('conversation');
            scrollToLatestMessage();
          });
      } else {
        dispatch({ type: 'chatbot.error-occured', payload: noAdapterError('switch to existing conversation') });
      }
    },
    [botAdapter, chatbotContext],
  );

  // Start a new conversation from scratch
  const startNewConversation = () => {
    dispatch({ type: 'chatbot.start-new-conversation' });
    setPanelDisplayed('conversation');
    registerChatbotEvent('conversation.new-conversation');
  };

  // Switch between Conversation and History panels
  const changePanelDisplayed = (panel: ChatbotPanelDisplayed) => {
    // When going on history panel, get the history on the first time
    if (panel === 'history' && context.history === undefined) {
      if (botAdapter) {
        botAdapter
          .getHistory(chatbotContext)
          .then((data) => {
            dispatch({ type: 'chatbot.set-conversations-history', payload: data.conversations });
          })
          .catch((error) => dispatch({ type: 'chatbot.error-occured', payload: error }));
      } else {
        dispatch({ type: 'chatbot.error-occured', payload: noAdapterError('get conversation history') });
      }
    }

    setPanelDisplayed(panel);
    scrollToLatestMessage();
  };

  // On initialization, we retrieve the conversation if we have a conversationId
  useEffect(() => {
    if (conversationId && !context.conversationId && !conversationIdRetrieved.current) {
      dispatch({ type: 'chatbot.waiting-for-bot' });
      switchToConversation(conversationId);
      conversationIdRetrieved.current = true;
    }
  }, [conversationId, context.conversationId, switchToConversation]);

  // On initialization, we start the discussion if we have an initial message
  useEffect(() => {
    if (!!initialMessage && !conversationId && context.messages.length === 0 && !initialMessageSent.current && botAdapter) {
      sendMessage(initialMessage);
      initialMessageSent.current = true;
    }
  }, [initialMessage, sendMessage, context.messages.length, conversationId, botAdapter]);

  // On initialization, we start the discussion if we have an initial message
  useEffect(() => {
    if (chatBeforeContactUs && botAdapter) {
      // Welcome message, displayed when the chat is opening
      if (count.user === 0 && !contactUsWelcomeRef.current) {
        dispatch({ type: 'information.display', payload: translate('contact-us.welcome') });
        contactUsWelcomeRef.current = true;
      }
      // Once we get the first bot message, we display the feedback request, after a small delay.
      if (count.bot === 1) {
        setTimeout(() => {
          setDisplayContactUsButton(true);
        }, 2000);
      }
    }
  }, [chatBeforeContactUs, botAdapter, count, translate]);

  const feedbackContactUs = (gotoContactUs: boolean) => {
    if (gotoContactUs) {
      // The feedback is negative, redirect the user to the Contact Us form.
      const helpCenter = document.querySelector<SgwtHelpCenterPublicFields & Element>('sgwt-help-center');
      const allMessages = context.messages.filter((msg): msg is ConversationMessage => msg.type === 'message');

      const conversation: string = allMessages
        .map((msg) => {
          const head = `${msg.content.role.toUpperCase()}${msg.content.id ? ` (${msg.content.id})` : ''}`;
          return `${head}\n${msg.content.message.content}`;
        })
        .join('\n\n');

      // Add context information to the Help-Center form.
      helpCenter?.setAdditionalMessageInformation({
        conversationId: context.conversationId,
        conversation,
      });
      helpCenter?.form({
        // Put the last user message as the main message.
        content: allMessages.findLast((msg) => msg.content.role === 'user')?.content.message.content,
        bypassChatbot: true,
      });
      registerChatbotEvent('feedback.contact-us', { conversationId });
    } else {
      // The feedback is positive, continue the conversation.
      setDisplayContactUsButton(false);
    }
  };

  // --- User reaction management ---
  const addReaction = (message: SgwtChatbotMessage, reaction: UserFeedbackReaction) => {
    if (supportFeedback(botAdapter)) {
      dispatch({ type: 'feedback.requested', payload: { reaction, message } });
      scrollToLatestMessage();
      registerChatbotEvent(`feedback.${reaction}.click`, { conversationId: message.conversationId, messageId: message.id });
    }
  };

  const sendFeedback = (feedback: UserFeedback) => {
    dispatch({
      type: 'feedback.submitted',
      payload: {
        ...feedback,
        status: 'submitting',
      },
    });

    // Get the conversation between the user and the bot, limited to messages before the feedback message
    let messageFound = false;
    const discussionSoFar: SgwtChatbotMessage[] = context.messages
      .filter((msg): msg is ConversationMessage => {
        if (msg.type === 'message' && !messageFound) {
          messageFound = msg.content.id === feedback.message.id;
          return true;
        }
        return false;
      })
      .map((msg: ConversationMessage) => msg.content);

    botAdapter
      ?.onUserFeedback?.(chatbotContext, feedback.message, feedback.reaction, feedback.explanations, discussionSoFar)
      .then(() => {
        dispatch({
          type: 'feedback.submitted',
          payload: {
            ...feedback,
            status: 'submitted',
          },
        });
        registerChatbotEvent(`feedback.${feedback.reaction}.click`, {
          conversationId: feedback.message.conversationId,
          messageId: feedback.message.id,
        });
      })
      .catch(() => {
        dispatch({
          type: 'feedback.submitted',
          payload: {
            ...feedback,
            status: 'error',
          },
        });
        registerChatbotEvent(`feedback.${feedback.reaction}.error`, {
          conversationId: feedback.message.conversationId,
          messageId: feedback.message.id,
        });
      });
  };

  const cancelFeedback = (feedback: UserFeedback) => {
    dispatch({ type: 'feedback.cancelled', payload: feedback });
  };

  return (
    <BotConversationContext.Provider
      value={{
        ...context,
        sendMessage,
        startNewConversation,
        switchToConversation,
        panelDisplayed,
        changePanelDisplayed,
        inputText,
        setInputText,
        addReaction,
        sendFeedback,
        cancelFeedback,
        feedbackContactUs,
        displayContactUsButton,
        isEmbeddedInHelpCenter: embeddedInHelpCenter,
      }}
    >
      {children}
    </BotConversationContext.Provider>
  );
};

export function useBotConversation() {
  return useContext(BotConversationContext);
}
